import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Card, Checkbox, Col, Divider, Form,
  Input, message, Modal, Popconfirm, Row, Select, Space,
  Table, Tag, Typography,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api/client';
import useAuthStore from '../store/authStore';
import CounterpartyInfoDrawer from '../components/CounterpartyInfoDrawer';
import IndividualInfoDrawer from '../components/IndividualInfoDrawer';

const { Title } = Typography;

// ── Types ────────────────────────────────────────────────────────────────────

interface IndividualRole {
  role: string;
  label: string;
  counterparty_id: number | null;
  counterparty_name: string | null;
}

interface Individual {
  id: number;
  last_name: string;
  first_name: string;
  middle_name?: string;
  gender?: string;
  job_title?: string;
  notes?: string;
  is_active: boolean;
  phones: string[];
  emails: string[];
  roles: IndividualRole[];
  tab_number?: string;
  birth_date?: string;
  hire_date?: string;
  position?: { id: number; name: string };
  department?: { id: number; name: string };
  organization?: string;
  status?: string;
}

// ── Constants ────────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  employee: 'Сотрудник',
  client: 'Клиент',
  counterparty_employee: 'Сотрудник контрагента',
};

const ROLE_COLORS: Record<string, string> = {
  employee: 'blue',
  client: 'green',
  counterparty_employee: 'orange',
};

// ── ContactList subcomponent ──────────────────────────────────────────────────

const ContactList: React.FC<{
  items: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
}> = ({ items, onChange, placeholder }) => {
  const add = () => onChange([...items, '']);
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i: number, v: string) => {
    const next = [...items];
    next[i] = v;
    onChange(next);
  };
  const isLast = (i: number) => i === items.length - 1;
  return (
    <div>
      {items.map((val, i) => (
        <Row key={i} gutter={8} style={{ marginBottom: 8 }}>
          <Col flex="auto">
            <Input value={val} placeholder={placeholder} onChange={e => update(i, e.target.value)} />
          </Col>
          {!isLast(i) && (
            <Col>
              <Button icon={<DeleteOutlined />} danger size="small" onClick={() => remove(i)} />
            </Col>
          )}
          {isLast(i) && (
            <>
              {items.length > 1 && (
                <Col>
                  <Button icon={<DeleteOutlined />} danger size="small" onClick={() => remove(i)} />
                </Col>
              )}
              <Col>
                <Button icon={<PlusOutlined />} size="small" onClick={add} />
              </Col>
            </>
          )}
        </Row>
      ))}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const IndividualsPage: React.FC = () => {
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('individuals:write');

  const [items, setItems] = useState<Individual[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState<string | undefined>();
  const [includeInactive, setIncludeInactive] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRefreshKey, setDrawerRefreshKey] = useState(0);
  const [selected, setSelected] = useState<Individual | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [phones, setPhones] = useState<string[]>([]);
  const [emails, setEmails] = useState<string[]>([]);
  const [counterparties, setCounterparties] = useState<{ id: number; short_name: string }[]>([]);
  const [saving, setSaving] = useState(false);

  // Counterparty info drawer
  const [cpDrawerOpen, setCpDrawerOpen] = useState(false);
  const [selectedCpId, setSelectedCpId] = useState<number | null>(null);

  const openCpDrawer = (cpId: number) => {
    setSelectedCpId(cpId);
    setCpDrawerOpen(true);
  };

  // Secondary individual drawer (opened from counterparty info)
  const [indInfoOpen, setIndInfoOpen] = useState(false);
  const [selectedIndId, setSelectedIndId] = useState<number | null>(null);

  // Load counterparties for the role form
  useEffect(() => {
    api.get('/counterparties', { params: { per_page: 100 } })
      .then(r => setCounterparties(r.data.items || []))
      .catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, per_page: 20 };
      if (search) params.search = search;
      if (filterRole) params.role = filterRole;
      if (includeInactive) params.include_inactive = true;
      const res = await api.get('/individuals', { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterRole, includeInactive]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    setPhones(['']);
    setEmails(['']);
    form.setFieldsValue({ roles: [{ role: 'client' }] });
    setModalOpen(true);
  };

  const openEdit = (ind: Individual) => {
    setEditingId(ind.id);
    form.setFieldsValue({
      last_name: ind.last_name,
      first_name: ind.first_name,
      middle_name: ind.middle_name,
      gender: ind.gender,
      job_title: ind.job_title,
      notes: ind.notes,
      roles: ind.roles.map(r => ({
        role: r.role,
        counterparty_id: r.counterparty_id,
      })),
    });
    setPhones(ind.phones.length > 0 ? [...ind.phones] : ['']);
    setEmails(ind.emails.length > 0 ? [...ind.emails] : ['']);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        phones: phones.filter(p => p.trim()),
        emails: emails.filter(e => e.trim()),
        roles: (values.roles || []),
      };
      if (editingId) {
        const res = await api.put(`/individuals/${editingId}`, payload);
        setItems(prev => prev.map(i => i.id === editingId ? res.data : i));
        if (selected?.id === editingId) {
          setSelected(res.data);
          setDrawerRefreshKey(prev => prev + 1);
        }
        message.success('Обновлено');
      } else {
        const res = await api.post('/individuals', payload);
        setItems(prev => [res.data, ...prev]);
        message.success('Физлицо создано');
      }
      setModalOpen(false);
    } catch (e: any) {
      if (e?.response?.data?.detail) message.error(e.response.data.detail);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (id: number) => {
    try {
      await api.delete(`/individuals/${id}`);
      if (selected?.id === id) setSelected(prev => prev ? { ...prev, is_active: false } : null);
      loadData();
      message.success('Архивировано');
    } catch {
      message.error('Ошибка');
    }
  };

  const fullName = (ind: Individual) =>
    [ind.last_name, ind.first_name, ind.middle_name].filter(Boolean).join(' ');

  const columns = [
    {
      title: 'ФИО',
      key: 'name',
      render: (_: any, r: Individual) => fullName(r),
    },
    {
      title: 'Роли',
      key: 'roles',
      render: (_: any, r: Individual) =>
        r.roles.length > 0
          ? r.roles.map(role => (
              <Tag key={role.role} color={ROLE_COLORS[role.role]}>{role.label}</Tag>
            ))
          : <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      title: 'Телефон',
      key: 'phone',
      render: (_: any, r: Individual) => r.phones[0] || <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      title: 'Статус',
      key: 'status',
      width: 100,
      render: (_: any, r: Individual) =>
        r.is_active ? <Tag color="green">Активен</Tag> : <Tag color="default">Архив</Tag>,
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={4} style={{ margin: 0 }}>Физические лица</Title></Col>
        {canWrite && (
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Добавить
            </Button>
          </Col>
        )}
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle">
          <Col span={8}>
            <Input.Search
              placeholder="Поиск по ФИО..."
              onSearch={v => { setSearch(v); setPage(1); }}
              onChange={e => { if (!e.target.value) { setSearch(''); setPage(1); } }}
              allowClear
            />
          </Col>
          <Col span={6}>
            <Select
              placeholder="Роль"
              allowClear
              style={{ width: '100%' }}
              onChange={v => { setFilterRole(v); setPage(1); }}
              options={Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }))}
            />
          </Col>
          <Col>
            <Checkbox
              checked={includeInactive}
              onChange={e => { setIncludeInactive(e.target.checked); setPage(1); }}
            >
              Показать архивных
            </Checkbox>
          </Col>
        </Row>
      </Card>

      <Table
        dataSource={items}
        columns={columns}
        rowKey="id"
        loading={loading}
        onRow={record => ({
          onClick: () => { setSelected(record); setDrawerOpen(true); },
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          total,
          pageSize: 20,
          onChange: p => setPage(p),
          showTotal: t => `Всего ${t}`,
        }}
      />

      {/* ── Drawer ── */}
      <IndividualInfoDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        individualId={selected?.id ?? null}
        refreshKey={drawerRefreshKey}
        extra={
          canWrite && selected ? (
            <Space>
              <Button type="primary" onClick={() => openEdit(selected)}>
                Редактировать
              </Button>
              {selected.is_active && (
                <Popconfirm
                  title="Архивировать?"
                  onConfirm={() => { handleArchive(selected.id); setDrawerOpen(false); }}
                  okText="Да"
                  cancelText="Нет"
                >
                  <Button danger>В архив</Button>
                </Popconfirm>
              )}
            </Space>
          ) : null
        }
        onCounterpartyClick={openCpDrawer}
      />

      {/* ── Modal ── */}
      <Modal
        title={editingId ? 'Редактировать физлицо' : 'Новое физлицо'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText={editingId ? 'Сохранить' : 'Создать'}
        cancelText="Отмена"
        confirmLoading={saving}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          {/* ФИО */}
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="last_name" label="Фамилия" rules={[{ required: true, message: 'Обязательное' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="first_name" label="Имя" rules={[{ required: true, message: 'Обязательное' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="middle_name" label="Отчество">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="gender" label="Пол">
                <Select allowClear options={[{ value: 'М', label: 'Мужской' }, { value: 'Ж', label: 'Женский' }]} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain style={{ fontSize: 13, color: '#888', margin: '8px 0' }}>
            Контакты
          </Divider>

          <Form.Item label="Телефон">
            <ContactList items={phones} onChange={setPhones} placeholder="+79001234567" />
          </Form.Item>

          <Form.Item label="Email">
            <ContactList items={emails} onChange={setEmails} placeholder="email@example.com" />
          </Form.Item>

          <Divider orientation="left" plain style={{ fontSize: 13, color: '#888', margin: '8px 0' }}>
            Работа
          </Divider>

          <Form.Item name="job_title" label="Должность">
            <Input placeholder="Менеджер по продажам" />
          </Form.Item>

          <Form.Item name="notes" label="Заметки">
            <Input.TextArea rows={2} />
          </Form.Item>

          {/* Роли */}
          <Form.Item label="Роли">
            <Form.List name="roles">
              {(fields, { add, remove }) => (
                <div>
                  {fields.map(({ key, name }) => (
                    <Row key={key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                      <Col span={8}>
                        <Form.Item name={[name, 'role']} noStyle>
                          <Select
                            style={{ width: '100%' }}
                            options={Object.entries(ROLE_LABELS).map(([v, l]) => ({ value: v, label: l }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, cur) =>
                            prev.roles?.[name]?.role !== cur.roles?.[name]?.role
                          }
                        >
                          {() => {
                            const role = form.getFieldValue(['roles', name, 'role']);
                            return role === 'counterparty_employee' ? (
                              <Form.Item name={[name, 'counterparty_id']} noStyle>
                                <Select
                                  style={{ width: '100%' }}
                                  placeholder="Контрагент"
                                  options={counterparties.map(c => ({ value: c.id, label: c.short_name }))}
                                />
                              </Form.Item>
                            ) : null;
                          }}
                        </Form.Item>
                      </Col>
                      <Col>
                        <Button size="small" danger onClick={() => remove(name)}>✕</Button>
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ role: 'client' })}>
                    Добавить роль
                  </Button>
                </div>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>

      <CounterpartyInfoDrawer
        open={cpDrawerOpen}
        onClose={() => setCpDrawerOpen(false)}
        counterpartyId={selectedCpId}
        onContactClick={(id) => { setSelectedIndId(id); setIndInfoOpen(true); }}
      />

      <IndividualInfoDrawer
        open={indInfoOpen}
        onClose={() => setIndInfoOpen(false)}
        individualId={selectedIndId}
        onCounterpartyClick={(cpId) => { setIndInfoOpen(false); openCpDrawer(cpId); }}
      />
    </div>
  );
};

export default IndividualsPage;
